import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import CookieBanner from "@/components/CookieBanner";
import SmoothScroll from "@/lib/SmoothScroll";
import { ThemeProvider } from "@/lib/ThemeContext";
import { AuthProvider } from "@/lib/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import StaticPage from "@/components/StaticPage";
import CartDrawer from "@/components/CartDrawer";
import { CartProvider } from "@/lib/CartContext";
import { SEO_MAP } from "@/lib/seoMap";

import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import WorkPage from "@/pages/WorkPage";
import IndustriesPage from "@/pages/IndustriesPage";
import AboutPage from "@/pages/AboutPage";
import InsightsPage from "@/pages/InsightsPage";
import InsightPost from "@/pages/InsightPost";
import ContactPage from "@/pages/ContactPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ClientPortal from "@/pages/ClientPortal";
import AdminDashboard from "@/pages/AdminDashboard";
import HostingPage from "@/pages/HostingPage";
import EmailPage from "@/pages/EmailPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const chromeless = pathname.startsWith("/admin") || pathname === "/login" || pathname === "/register";
  return (
    <>
      <Nav />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/services" element={<ServicesPage />} />
        <Route path="/hosting" element={<HostingPage />} />
        <Route path="/email" element={<EmailPage />} />
        <Route path="/work" element={<WorkPage />} />
        <Route path="/industries" element={<IndustriesPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/insights" element={<InsightsPage />} />
        <Route path="/insights/:slug" element={<InsightPost />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/portal" element={<ProtectedRoute><ClientPortal /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
        <Route path="/privacy" element={<StaticPage seo={SEO_MAP.privacy} overline="Privacy" title={<>Privacy<br /><em>Policy</em>.</>} body={PRIVACY} />} />
        <Route path="/terms" element={<StaticPage seo={SEO_MAP.terms} overline="Terms" title={<>Terms &<br /><em>Conditions</em>.</>} body={TERMS} />} />
        <Route path="/careers" element={<StaticPage seo={SEO_MAP.careers} overline="Careers" title={<>Build with<br /><em>us</em>.</>} body={CAREERS} />} />
      </Routes>
      {!chromeless && <Footer />}
      <WhatsAppFab />
      <CookieBanner />
    </>
  );
}

const PRIVACY = `Last updated: December 2025

At LaneX Labs — the technology and digital solutions division of LaneX Holdings — your privacy is fundamental to how we operate. This Privacy Policy explains how we collect, use, share and protect the personal information you provide when engaging with our website, project enquiry system and client portal.

1. Information we collect
We collect only the information necessary to deliver our services. This includes contact details you voluntarily provide through our enquiry form (name, email, phone, company), the content of your project brief, authentication data for the client portal (email, hashed password) and standard analytics data on how visitors interact with our site (pages viewed, referrer, device type, aggregated performance metrics).

2. How we use your information
Your data is used to respond to your enquiry within the promised 24-hour SLA, to scope, plan and deliver contracted projects, to operate the client portal (projects, invoices, tickets, files, messaging), to send transactional and service-related communications, and to improve our website and services based on aggregated analytics.

3. Data sharing and third parties
We do not sell your data. We share data only with subprocessors strictly necessary to deliver our services — for example, cloud infrastructure providers, payment processors and communication tools — each bound by data-processing agreements. A current list of subprocessors is available on request.

4. Data retention
Enquiry data is retained for 24 months from last contact. Client project data is retained for the duration of engagement plus seven years for accounting, tax and warranty purposes. Portal accounts remain until deletion is requested by the account holder.

5. Your rights
Under UK GDPR and applicable regulations you have the right to access, correct, port, restrict processing of, or delete your personal data. Contact hello@lanexlabs.com to exercise any of these rights.

6. Security
We apply industry-standard technical and organisational controls including encryption in transit, encrypted secrets, principle of least privilege, and continuous monitoring on production systems.

7. Cookies
We use essential cookies to operate the site and optional analytics cookies to understand aggregate usage. You can accept or reject optional cookies via our banner at any time.

8. Contact
LaneX Labs · Data Protection · hello@lanexlabs.com — we reply within two business days.`;

const TERMS = `Last updated: December 2025

These Terms & Conditions govern your use of the LaneX Labs website, project enquiry system, client portal and services. By engaging LaneX Labs (a division of LaneX Holdings) you agree to the terms below in addition to the specific Statement of Work signed for your project.

1. Services
LaneX Labs delivers websites, e-commerce platforms, custom software, automation, cloud & infrastructure, cybersecurity and ongoing support. All work is scoped and priced individually via a written Statement of Work (SoW).

2. Engagement
Engagement commences when both parties sign the SoW and the initial deposit clears. The SoW specifies scope, milestones, deliverables, timelines, payment schedule and success criteria. Any changes are handled via a written change-order process.

3. Payment
Standard payment terms are 30 days from invoice date unless otherwise agreed. Overdue invoices accrue interest per the UK Late Payment of Commercial Debts (Interest) Act. Milestone-based projects require deposits and staged payments as defined in the SoW.

4. Intellectual property
On full payment, all custom-developed code and design assets created specifically for your project are assigned to you. Third-party libraries and pre-existing LaneX Labs frameworks or components remain under their respective licences.

5. Confidentiality
Both parties agree to keep confidential any non-public information exchanged during engagement. Mutual NDAs are available on request before scoping discussions.

6. Warranties and liability
We warrant that our work will materially conform to the SoW. Post-launch defects reported within 30 days will be remediated at no additional cost. Our aggregate liability is limited to fees paid under the applicable SoW during the preceding twelve months, except for cases of gross negligence, wilful misconduct or breach of confidentiality.

7. Support & maintenance
Ongoing support is provided under a separate support plan with defined SLAs. Emergency response times, coverage hours and quotas are specified in the plan.

8. Termination
Either party may terminate an engagement with 30 days' written notice. Fees for work completed up to the effective termination date remain payable.

9. Governing law
These terms are governed by the laws of England & Wales. Disputes are subject to the exclusive jurisdiction of the London courts.

10. Contact
LaneX Labs Legal · legal@lanexlabs.com`;

const CAREERS = `Careers at LaneX Labs

We are always interested in exceptional engineers, designers and operators who take craft seriously and treat delivery as the discipline that separates good from great.

Who we look for
We hire senior generalists and specialists across full-stack engineering (React, Node, Python, Go), product design and motion, DevOps and cloud architecture (AWS, GCP), automation engineering, cybersecurity, and technical project ownership. We value people who can move between the strategy conversation and the codebase — and who prefer to ship one thing exceptionally rather than ten things adequately.

How we work
We work in small, senior teams — usually two to five people per project — with high context and low overhead. We favour written thinking, weekly demos and shared previews over status meetings. We work remotely with light overlap across UK, EU and West Africa timezones. Our stack rotates by project need but our practices don't: performance budgets, accessibility from day one, motion systems, observability, and design decisions defended with data.

What we offer
Competitive salary benchmarked to London and remote markets, meaningful equity in the LaneX Holdings group where appropriate, a personal development budget for books, courses and conferences, top-tier hardware, and time protected for deep work. We do not run open-plan calendars.

How to apply
Send us your portfolio, GitHub, or a two-minute Loom introducing yourself and one project you're proud of. Include the role you're interested in and where you're based. We reply to every application within ten business days.

careers@lanexlabs.com`;

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CartProvider>
          <SmoothScroll>
            <BrowserRouter>
              <div className="App min-h-screen">
                <Shell />
                <CartDrawer />
                <Toaster />
              </div>
            </BrowserRouter>
          </SmoothScroll>
        </CartProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
